import cn from 'classnames'
import { FC, ReactNode } from 'react'
import { parseError } from 'common/utils/parseError'
import {
  CircleCheckSuccessIcon,
  CircleXIcon,
  InfoIcon,
  WarningIcon,
} from 'project/icons'

interface MessageProps {
  children?: ReactNode | any
  className?: string
  'data-test'?: string
  icon?: ReactNode | string
}

const Message: FC<MessageProps> = ({
  children,
  className,
  'data-test': dataTest,
  icon,
}) => {
  if (!children) {
    return null
  }

  const renderIcon = () => {
    if (!icon) return null
    if (typeof icon === 'string') {
      return <span className={cn('icon', 'me-3', icon)} />
    }
    return icon
  }

  const renderContent = () => {
    if (typeof children === 'string') {
      return (
        <span
          style={{ overflowWrap: 'anywhere' }}
          dangerouslySetInnerHTML={{ __html: children.replace(/\n/g, '') }}
          data-test='message'
        />
      )
    }
    return children
  }

  return (
    <div
      data-test={dataTest}
      className={cn('px-4 py-2 fs-lg text-white fw-semibold', className)}
    >
      <div className='flex-row align-items-center'>
        {renderIcon()}
        {renderContent()}
      </div>
    </div>
  )
}

Message.displayName = 'Message'

export const ErrorMessage: FC<MessageProps> = ({ className, ...props }) => {
  if (!props.children) return null

  return (
    <Message
      {...props}
      icon={props.icon ?? <WarningIcon className='me-2' />}
      className={cn(className, 'bg-danger border-danger')}
    >
      {parseError(props.children)}
    </Message>
  )
}

ErrorMessage.displayName = 'ErrorMessage'

export const SuccessMessage: FC<MessageProps> = ({ className, ...props }) => {
  if (!props.children) return null

  return (
    <Message
      {...props}
      icon={props.icon ?? <CircleCheckSuccessIcon className='me-2' />}
      className={cn(className, 'bg-success border-success')}
    />
  )
}

SuccessMessage.displayName = 'SuccessMessage'

export const InfoMessage: FC<MessageProps> = ({ className, ...props }) => {
  if (!props.children) return null

  return (
    <Message
      {...props}
      icon={props.icon ?? <InfoIcon className='me-2' />}
      className={cn(className, 'bg-info border-info')}
    />
  )
}

InfoMessage.displayName = 'InfoMessage'

export const WarningMessage: FC<MessageProps> = ({ className, ...props }) => {
  if (!props.children) return null

  return (
    <Message
      {...props}
      icon={props.icon ?? <WarningIcon className='me-2' />}
      className={cn(className, 'bg-warning border-warning')}
    />
  )
}

WarningMessage.displayName = 'WarningMessage'
